
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Upload, Users, Mail, Phone, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Guest {
  email?: string;
  phone?: string;
  name?: string;
}

interface BulkGuestManagerProps {
  guests: Guest[];
  onGuestsChange: (guests: Guest[]) => void;
}

export const BulkGuestManager: React.FC<BulkGuestManagerProps> = ({
  guests,
  onGuestsChange
}) => {
  const [bulkInput, setBulkInput] = useState('');
  const { toast } = useToast();

  const parseEmailList = (text: string): Guest[] => {
    const lines = text.split('\n').filter(line => line.trim());
    const parsed: Guest[] = [];

    lines.forEach(line => {
      const trimmed = line.trim();
      
      // Try to parse email
      const emailMatch = trimmed.match(/([^\s,]+@[^\s,]+)/);
      if (emailMatch) {
        const email = emailMatch[1];
        // Try to extract name (text before email or after email)
        const beforeEmail = trimmed.substring(0, trimmed.indexOf(email)).trim();
        const nameMatch = beforeEmail.match(/^([^<]+)/) || trimmed.match(/^([^,]+)/);
        
        parsed.push({
          email,
          name: nameMatch ? nameMatch[1].trim().replace(/[<>"]/g, '') : undefined
        });
        return;
      }

      // Try to parse phone
      const phoneMatch = trimmed.match(/[\+]?[1-9][\d\s\-\(\)]{8,}/);
      if (phoneMatch) {
        const phone = phoneMatch[0];
        const name = trimmed.replace(phone, '').trim().replace(/[,]/g, '');
        
        parsed.push({
          phone,
          name: name || undefined
        });
        return;
      }

      // If no email or phone, treat as name with email domain
      if (trimmed && !trimmed.includes('@') && !phoneMatch) {
        parsed.push({ name: trimmed });
      }
    });

    return parsed;
  };

  const handleBulkAdd = () => {
    if (!bulkInput.trim()) return;

    const newGuests = parseEmailList(bulkInput);
    
    if (newGuests.length === 0) {
      toast({
        title: "No valid contacts found",
        description: "Please check the format and try again.",
        variant: "destructive",
      });
      return;
    }

    // Merge with existing guests, avoiding duplicates
    const existingEmails = new Set(guests.map(g => g.email).filter(Boolean));
    const existingPhones = new Set(guests.map(g => g.phone).filter(Boolean));
    
    const uniqueNewGuests = newGuests.filter(guest => {
      if (guest.email && existingEmails.has(guest.email)) return false;
      if (guest.phone && existingPhones.has(guest.phone)) return false;
      return true;
    });

    onGuestsChange([...guests, ...uniqueNewGuests]);
    setBulkInput('');
    
    toast({
      title: "Guests added",
      description: `Added ${uniqueNewGuests.length} new guests.`,
    });
  };

  const removeGuest = (index: number) => {
    const updatedGuests = guests.filter((_, i) => i !== index);
    onGuestsChange(updatedGuests);
  };

  const clearAll = () => {
    onGuestsChange([]);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Bulk Add Guests
          </CardTitle>
          <CardDescription>
            Add multiple guests at once. Paste email addresses, phone numbers, or names (one per line).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder={`john.doe@email.com
Jane Smith <jane@email.com>
+1 234 567 8900
Alice Johnson
bob@company.com`}
            value={bulkInput}
            onChange={(e) => setBulkInput(e.target.value)}
            rows={6}
          />
          <div className="flex gap-2">
            <Button onClick={handleBulkAdd} disabled={!bulkInput.trim()}>
              <Upload className="w-4 h-4 mr-2" />
              Add Guests
            </Button>
            <Button variant="outline" onClick={() => setBulkInput('')}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {guests.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <CardTitle>Guest List ({guests.length})</CardTitle>
              </div>
              <Button variant="destructive" size="sm" onClick={clearAll}>
                <Trash2 className="w-4 h-4 mr-2" />
                Clear All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {guests.map((guest, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {guest.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{guest.email}</span>
                        </div>
                      )}
                      {guest.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{guest.phone}</span>
                        </div>
                      )}
                    </div>
                    {guest.name && (
                      <Badge variant="secondary">{guest.name}</Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeGuest(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
